import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { RedditMentionDto } from '../../domain/entities/RedditMention';
import { PaginatedResult } from '../../domain/common/PaginatedResult';
import {
  CustomerMentionRepository,
  PendingMentionsQuery,
  CustomerMentionActionResponse
} from '../../infrastructure/repositories/CustomerMentionRepository';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { notificationService } from '../../infrastructure/notifications';
import { queryKeys } from './queryKeys';
import { CACHE_CONFIG } from './queryClient';

/**
 * Repository instance (singleton)
 */
const customerMentionRepository = new CustomerMentionRepository(apiClient);

/**
 * Confirm mention mutation params
 */
interface ConfirmMentionParams {
  businessId: string;
  mentionId: string;
}

/**
 * Reject mention mutation params
 */
interface RejectMentionParams {
  businessId: string;
  mentionId: string;
  reason: string;
  additionalComments?: string;
}

/**
 * Hook to fetch pending customer mentions
 *
 * Returns mentions that:
 * - Are matched to the specified business
 * - Have high confidence (≥50%)
 * - Are pending customer confirmation
 *
 * Sprint 037 Session 9: Customer self-confirmation UI
 *
 * @param businessId - The business ID to fetch mentions for
 * @param query - Optional pagination parameters
 * @param enabled - Whether the query should run (default: true if businessId is provided)
 */
export const useCustomerPendingMentionsQuery = (
  businessId: string | null,
  query?: PendingMentionsQuery,
  enabled?: boolean
): UseQueryResult<PaginatedResult<RedditMentionDto>, Error> => {
  const shouldEnable = enabled !== undefined ? enabled : !!businessId;

  return useQuery({
    queryKey: queryKeys.customerMentions.pending(businessId, query),
    queryFn: async (): Promise<PaginatedResult<RedditMentionDto>> => {
      if (!businessId) {
        return {
          items: [],
          pageNumber: 1,
          pageSize: 10,
          totalCount: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false
        };
      }
      return customerMentionRepository.getPendingMentions(
        businessId,
        query?.pageNumber ?? 1,
        query?.pageSize ?? 10
      );
    },
    staleTime: CACHE_CONFIG.MENTIONS.staleTime,
    gcTime: CACHE_CONFIG.MENTIONS.gcTime,
    enabled: shouldEnable && !!businessId
  });
};

/**
 * Mutation hook for customer to confirm a mention
 *
 * Marks a mention as confirmed by the business owner and creates an audit record.
 *
 * Sprint 037 Session 9: Customer self-confirmation UI
 */
export const useConfirmMentionMutation = (): UseMutationResult<
  CustomerMentionActionResponse,
  Error,
  ConfirmMentionParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, mentionId }: ConfirmMentionParams): Promise<CustomerMentionActionResponse> => {
      return customerMentionRepository.confirmMention(businessId, mentionId);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all customer mention queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.customerMentions.all });

      // Invalidate regular mention queries as well (for MentionsPage)
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.all });

      // Invalidate business detail if cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.businessId) });

      // Show success notification
      notificationService.showSuccess('Mention has been confirmed', 'Mention Confirmed');
    },
    onError: (error: Error): void => {
      console.error('Confirm mention failed:', error);
      notificationService.showError('Failed to confirm mention. Please try again.', 'Error');
    }
  });
};

/**
 * Mutation hook for customer to reject a mention
 *
 * Marks a mention as rejected by the business owner with a reason.
 * The reason is required and can be one of:
 * - "Not my business"
 * - "Wrong location"
 * - "Spam/Irrelevant"
 * - "Other"
 *
 * Sprint 037 Session 9: Customer self-confirmation UI
 */
export const useRejectMentionMutation = (): UseMutationResult<
  CustomerMentionActionResponse,
  Error,
  RejectMentionParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      businessId,
      mentionId,
      reason,
      additionalComments
    }: RejectMentionParams): Promise<CustomerMentionActionResponse> => {
      return customerMentionRepository.rejectMention(businessId, mentionId, reason, additionalComments);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all customer mention queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.customerMentions.all });

      // Invalidate regular mention queries as well (for MentionsPage)
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.all });

      // Invalidate business detail if cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.businessId) });

      // Show success notification
      notificationService.showSuccess('Mention has been rejected', 'Mention Rejected');
    },
    onError: (error: Error): void => {
      console.error('Reject mention failed:', error);
      notificationService.showError('Failed to reject mention. Please try again.', 'Error');
    }
  });
};
